import { createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash-es';
import { message } from '@docon/docon-ui-library';
import { Status } from '.';
import { createAsyncAction } from '../action';

const login = createAsyncAction('login', 'login');
const logout = createAsyncAction('logout', 'logout');
const resetPassword = createAsyncAction('resetPassword', 'resetPassword');
const checkResetPasswordSessionToken = createAsyncAction('checkResetPasswordSessionToken', 'checkResetPasswordSessionToken');
const forgotPassword = createAsyncAction('forgotPassword', 'forgotPassword');
const refreshToken = createAsyncAction('refreshToken', 'refreshToken');

interface AuthUser {
  id: number;
  isAdmin: boolean;
  isStoreManager: boolean;
  isHR: boolean;
  refreshToken: string;
  roles: any;
  token: string;
  type: string;
  companyId: string;
  zone: string;
  city: string;
  storeId?: number;
  userName: string;
}

interface IInitialState {
  authUser?: AuthUser;
  accessToken: string;
  isLogging: boolean;
  loginStatus: string;
  logoutStatus: string;
  resetPasswordStatus: string;
  forgotPasswordStatus: string;
  checkResetPasswordSessionTokenStatus: string;
  refreshTokenSuccess: string;
  loginErrors: any;
}

const initialState = {
  authUser: {},
  accessToken: '',
  isLogging: false,
  loginStatus: '',
  logoutStatus: '',
  resetPasswordStatus: '',
  forgotPasswordStatus: '',
  checkResetPasswordSessionTokenStatus: '',
  refreshTokenSuccess: '',
  loginErrors: {},
} as IInitialState;

export const counterSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateAuthState: (state, action) => Object.assign(state, action.payload),
  },
  extraReducers: {
    [login.pending as any]: (state) => {
      state.isLogging = true;
      state.loginStatus = Status.pending;
    },
    [login.fulfilled as any]: (state, { payload }) => {
      const user: AuthUser = payload;
      user.roles.forEach(role => {
        switch (role) {
          case 'ROLE_ADMIN':
            user.isAdmin = true; break;
          case 'ROLE_HR':
            user.isHR = true; break;
          case 'ROLE_STORE':
            user.isStoreManager = true; break;
          default:
            break;
        }
      });
      state.isLogging = false;
      state.loginStatus = Status.fulfilled;
      state.accessToken = payload.token;
      state.authUser = user;
    },
    [login.rejected as any]: (state, { payload }) => {
      state.isLogging = false;
      state.loginStatus = Status.rejected;
      state.loginErrors = get(payload, 'errors', {});
    },
    [refreshToken.pending as any]: (state) => {
      state.refreshTokenSuccess = '';
    },
    [refreshToken.fulfilled as any]: (state, { payload }) => {
      state.refreshTokenSuccess = payload?.success || '';
    },
    [logout.pending as any]: (state) => {
      state.logoutStatus = Status.pending;
    },
    [logout.fulfilled as any]: (state) => {
      state.logoutStatus = Status.fulfilled;
    },
    [logout.rejected as any]: (state) => {
      state.logoutStatus = Status.rejected;
    },
    [resetPassword.pending as any]: (state) => {
      state.resetPasswordStatus = Status.pending;
    },
    [resetPassword.fulfilled as any]: (state) => {
      state.resetPasswordStatus = Status.fulfilled;
    },
    [resetPassword.rejected as any]: (state) => {
      state.resetPasswordStatus = Status.rejected;
    },

    [checkResetPasswordSessionToken.fulfilled as any]: (state) => {
      state.checkResetPasswordSessionTokenStatus = Status.fulfilled;
    },
    [checkResetPasswordSessionToken.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError') {
        state.checkResetPasswordSessionTokenStatus = Status.rejected;
      }
    },

    [forgotPassword.pending as any]: (state) => {
      state.forgotPasswordStatus = Status.pending;
    },
    [forgotPassword.fulfilled as any]: (state) => {
      state.forgotPasswordStatus = Status.fulfilled;
      message.success('Reset-Password Link sent to registered email successfully');
    },
    [forgotPassword.rejected as any]: (state, { error }) => {
      if (error.name !== 'AbortError') {
        state.forgotPasswordStatus = Status.rejected;
        // message.error('Reset-Password Link sent failed');
      }
    },
  },
});

const { updateAuthState } = counterSlice.actions;
export {
  updateAuthState,
  login,
  logout,
  resetPassword,
  checkResetPasswordSessionToken,
  forgotPassword,
  refreshToken,
};
export default counterSlice.reducer;
